import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { browserHistory, Link } from 'react-router';

import { ActivatedSubscriptionInfo } from 'src/app/subscription/subscription-types';
import { Button } from 'src/common/components/button/button';
import { Flex } from 'src/common/components/flex/flex';
import { Logo } from 'src/common/components/logo';
import { UserOutput } from 'src/common/types/user';

import Widget from './sign-in-widget';
import './sign-in.css';

type SignInOwnProps = {
  action?: 'signup' | 'login';
  viewParams: {
    query: {
      plan?: string;
      redirect?: string;
      signup_src?: string;
      src_internal?: string;
    };
  };
};

type SignInStateProps = {
  currentPlan: ActivatedSubscriptionInfo | null;
  userName?: string;
};

type SignInDispatchProps = {
  reset: () => void;
};

type SignInProps = SignInOwnProps & SignInStateProps & SignInDispatchProps;

const SignIn = ({
  action: reduxAction,
  currentPlan,
  reset,
  userName,
  viewParams: {
    query: { plan, redirect, signup_src: signupSrc, src_internal: srcInternal },
  },
}: SignInProps) => {
  const [action, setAction] = useState(reduxAction);
  const isLogin = action === 'login';

  useEffect(() => {
    if (userName) {
      if (redirect) {
        window.location.replace(decodeURIComponent(redirect));
      } else {
        browserHistory.push('/');
      }
    }
  }, [redirect, userName]);

  const done = (user?: UserOutput & { error?: string }) => {
    if (!user || user.error) {
      return;
    }

    if (plan) {
      if (currentPlan) {
        browserHistory.push('/?alert=already-subscribed');
        return;
      }
      browserHistory.replace(
        `/checkout?plan=${plan}&src_internal=${srcInternal}`
      );
      return;
    }

    if (redirect) {
      window.location.replace(decodeURIComponent(redirect));
      return;
    }

    if (isLogin) {
      browserHistory.push('/');
    } else {
      browserHistory.push(`/?signedup=true&signup_src=${signupSrc}`);
    }
  };

  return (
    <div className="signin-page">
      <Helmet>
        <title>
          {isLogin
            ? window._STM_PREDATA.login.title
            : window._STM_PREDATA.signup.title}
        </title>
        <meta
          name="description"
          content={
            isLogin
              ? window._STM_PREDATA.login.description
              : window._STM_PREDATA.signup.description
          }
        ></meta>
        <link rel="canonical" href={`https://streamable.com/${action}`} />
      </Helmet>
      <Flex alignItems="center" className="signin-box">
        <Logo
          type="black"
          theme={'light'}
          style={{
            width: '40px',
            height: '40px',
            objectPosition: 'left',
            objectFit: 'cover',
          }}
        />
        <h1>{isLogin ? 'Log in' : 'Sign up'}</h1>
        <p>
          to continue to <b>Streamable</b>
        </p>

        <Widget
          verificationRedirect="https://streamable.com?alert=verified"
          action={action}
          onDone={done}
        />
      </Flex>
      <div className="footer">
        <div>
          <span className="question">
            {isLogin ? "Don't have an account?" : 'Already have an account?'}
          </span>
          <Button
            variant="link"
            onClick={() => {
              const oppositeAction = isLogin ? 'signup' : 'login';
              const searchParams =
                typeof window !== 'undefined' ? window?.location?.search : '';
              const oppositeRoute = `/${oppositeAction}${searchParams}`;
              setAction(oppositeAction);
              window.history.pushState({}, '', oppositeRoute);
              reset();
            }}
          >
            <b>{isLogin ? 'Sign up for free.' : 'Log in'}</b>
          </Button>
        </div>
        <div>
          {isLogin && (
            <Link to="/recover" className="forgot-password">
              Forgot your password?
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignIn;
